import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Transition } from 'react-transition-group';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import { RatingReview } from '@/interfaces/Rating';
import { RatingIcons } from '@/src/components/icons';
import { FragmentConfiguration } from '@/config';
import useUrlParameters from '../../hooks/useUrlParameters';

const flyoutTransitionStyles = {
  entered: { transform: 'scale(1, 1)' },
  entering: { transform: 'scale(0, 0)' },
  exited: { transform: 'scale(0, 0)' },
  exiting: { transform: 'scale(1, 1)' },
};

const RatingHeadlineWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 1rem 1rem 0.5rem;
`;

const RatingHeadlineShoes = styled(RatingIcons)`
  width: 6.25rem;
`;

const RatingAuthorAndDate = styled.div`
  margin-top: 0.25rem;
  color: #a0a0a0;
`;

const RatingCardMenuIcon = styled.div`
  margin-left: auto;
  position: relative;
  align-self: flex-start;
`;

const DotWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  width: 1rem;
  > svg {
    fill: #000000;
    color: #000000;
  }
`;

const CardFlyout = styled.div`
  transform-origin: top right;
  transform: scale(0, 0);
  transition: all 300ms ease-in-out;
  background-color: #fff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 2px 1px -1px rgba(0, 0, 0, 0.12),
    0 1px 1px 0 rgba(0, 0, 0, 0.14);
  border: 1px solid #f4f4f4;
  position: absolute;
  z-index: 2;
  right: 0;
  bottom: -4.5rem;
  padding: 1.5rem 1rem;
  border-radius: 3px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  user-select: none;
`;

const ReportRating = styled.div`
  white-space: nowrap;
  &&.clickable {
    cursor: pointer;
  }
`;

const handleHelpfulClick = (url: string, setHasBeenRated: (boolean) => void) => {
  fetch(url)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .then((response: Response): Promise<any> => response.json())
    .then(
      (json): JSON => {
        setHasBeenRated(true);
        return json;
      },
    )
    .catch(() => {
      setHasBeenRated(true);
      return null;
    });
};

interface RatingHeadlineProps {
  rating: RatingReview;
  menu: boolean;
}

export const RatingHeadline: FunctionComponent<RatingHeadlineProps> = ({ rating, menu }) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false);
  const [hasBeenRated, setHasBeenRated] = useState<boolean>(false);
  const config: FragmentConfiguration = useConfig();
  const helpfulClickUrl = useUrlParameters(
    config.customerRatings.values.helpful,
    {
      action: 'inappropriate',
      reviewid: rating.id.toString(),
    },
    true,
  );

  const OverflowMenu = getIcon('MenuAndroid');

  return (
    <RatingHeadlineWrapper>
      <div>
        <RatingHeadlineShoes
          maskWidth={`${parseFloat(rating.rating.toFixed(1)) * 20}%`}
          data-testid="rating-card-headline-stars"
          iconType={config.customerRatings.iconType}
        />
        <RatingAuthorAndDate data-testid="rating-card-author-and-date">
          <FormattedMessage
            id="rating.author"
            defaultMessage="<nickname>von {authorNickName}</nickname> | {created}"
            values={{
              authorLocation: rating.reviewer && rating.reviewer.location,
              authorNickName: rating.reviewer && rating.reviewer.nickname,
              created: rating.created,
              nickname: (txt) => (rating.reviewer && rating.reviewer.nickname ? txt : null),
            }}
          />
        </RatingAuthorAndDate>
      </div>
      {menu && (
        <RatingCardMenuIcon data-testid="rating-card-menu">
          <DotWrapper onClick={() => setIsFlyoutOpen(!isFlyoutOpen)} data-testid="rating-card-link">
            <OverflowMenu size="2rem" />
          </DotWrapper>
          <Transition in={isFlyoutOpen} timeout={0}>
            {(state) => (
              <CardFlyout
                data-testid={`rating-card-flyout${isFlyoutOpen ? '-open' : ''}`}
                style={{ ...flyoutTransitionStyles[state] }}
              >
                {hasBeenRated ? (
                  <ReportRating data-testid="rating-has-been-rated">
                    <FormattedMessage
                      id="rating.rating-reported"
                      defaultMessage="Vielen Dank für deine Einschätzung."
                    />
                  </ReportRating>
                ) : (
                  <ReportRating
                    className="clickable"
                    onClick={() => handleHelpfulClick(helpfulClickUrl, setHasBeenRated)}
                  >
                    <FormattedMessage id="rating.report-rating" defaultMessage="Bewertung melden" />
                  </ReportRating>
                )}
              </CardFlyout>
            )}
          </Transition>
        </RatingCardMenuIcon>
      )}
    </RatingHeadlineWrapper>
  );
};

export default RatingHeadline;
